import {
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Tooltip,
  Divider,
  Snackbar,
  IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { Fragment, useState, forwardRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import MuiAlert from "@mui/material/Alert";
import HttpService from "../services/HttpService";
import CircularProgress from "@mui/material/CircularProgress";
import PaidIcon from "@mui/icons-material/Paid";
import BusinessIcon from "@mui/icons-material/Business";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NumbersIcon from "@mui/icons-material/Numbers";
import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import UserService from "../services/UserService";
import RenderOnRole from "./RenderOnRole";
import Labels from "../labels/Labels";

export default function PaymentsDetail(props) {
  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("payments.column.id", "ID"),
      width: 90,
      hidden: true,
    },
    {
      field: "lineNumber",
      headerName: Labels.getLabel("payments.lineNumber", "Line#"),
      width: 50,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.lineNumber}
          </Typography>
        );
      },
    },
    {
      field: "memberId",
      headerName: Labels.getLabel("payments.column.memberId", "Member Id"),
      width: 110,
      editable: UserService.hasRole(["pf-confirm"]),
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.memberId}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: Labels.getLabel("payments.column.email", "Email"),
      width: 180,
      editable: UserService.hasRole(["pf-confirm"]),
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.email}
          </Typography>
        );
      },
    },
    {
      field: "amount",
      headerName: Labels.getLabel("payments.column.amount", "Amount"),
      width: 90,
      align: "right",
      valueFormatter: (params) => {
        if (params.value == null) {
          return "";
        }

        const valueFormatted = Number(params.value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
        });
        return `${valueFormatted}`;
      },
    },
    {
      field: "fullName",
      headerName: Labels.getLabel("payments.column.fullName", "Name"),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.fullName}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel("payments.column.status", "Status"),
      width: 180,
      editable:
        UserService.hasRole(["pf-confirm"]) &&
        UserService.hasRole(["pc-program-super-admin"]),
      renderCell: (params) => {
        return (
          <div>
            <Typography
              sx={{ display: "block", fontWeight: "bold", fontSize: 12, mb: 0 }}
              gutterBottom
            >
              {params.row.status}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel("payments.column.updateDate", "Update Date"),
      width: 150,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.updateDate}
          </Typography>
        );
      },
    },
    {
      field: "idDescriptor",
      headerName: Labels.getLabel("payments.column.idDescriptor", "Descriptor"),
      width: 240,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.idDescriptor}
          </Typography>
        );
      },
    },
    {
      field: "error",
      headerName: Labels.getLabel("payments.column.error", "Error"),
      width: 300,

      renderCell: (params) => {
        return (
          <Tooltip title={params.row.error}>
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.error}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const queryClient = useQueryClient();

  const updatePaymentMutation = useMutation(
    (payload) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/payfile/${payload.idPayFile}/update`,
        payload
      );
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "payments.updateSuccess",
            "The request was verified succesfully"
          ),
          onClose: () => {
            refetchPaymentsDetail();
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
          onClose: () => {
            refetchPaymentsDetail();
            setFeedback({ open: false });
          },
        });
      },
    }
  );

  const handleEditCellChange = (row, id, field, oldValue, newValue) => {
    updatePaymentMutation.mutate({
      idPayFile: row.idPayFile,
      record: row,
      field,
      oldValue,
      newValue,
    });
  };

  const { paymentInfo } = props;
  async function fetchPaymentsDetail() {
    var url = window.API_URL + `/payfile/details/` + paymentInfo.idPayFile;
    if (paymentInfo.payFileDetail)
      url = url + "?lineNumber=" + paymentInfo.payFileDetail.lineNumber;
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  const {
    data: dataPaymentsDetail,
    isError: isErrorPaymentsDetail,
    isFetching: isFetchingPaymentsDetail,
    refetch: refetchPaymentsDetail,
  } = useQuery({
    queryKey: ["paymentsDetail"],
    queryFn: fetchPaymentsDetail,
    refetchOnWindowFocus: false,
  });

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    props.setOpen(false);
  };
  const totalAmountSuccess =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.status === "LOAD_SUCCESS" ||
          record.status === "READY_TO_NOTIFY"
      )
      .reduce((sum, record) => sum + record.amount, 0);

  const totalCountSuccess =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.status === "LOAD_SUCCESS" ||
          record.status === "READY_TO_NOTIFY"
      )
      .reduce((sum, record) => sum + 1, 0);

  const formattedAmountSuccess =
    totalAmountSuccess &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalAmountSuccess);

  const pendingAmountSuccess =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.status !== "LOAD_SUCCESS" &&
          record.status !== "READY_TO_NOTIFY" &&
          record.status !== "SPLITTED"
      )
      .reduce((sum, record) => sum + record.amount, 0);

  const totalCountPending =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.status !== "LOAD_SUCCESS" &&
          record.status !== "READY_TO_NOTIFY" &&
          record.status !== "SPLITTED"
      )
      .reduce((sum, record) => sum + 1, 0);

  const formattedAmountPending =
    pendingAmountSuccess &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(pendingAmountSuccess);

  const totalVisaDps =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.processorProvider === "visadps" && record.status !== "SPLITTED"
      )
      .reduce((sum, record) => sum + record.amount, 0);

  const formattedAmountVisaDps =
    totalVisaDps &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalVisaDps);

  const totalQolo =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.processorProvider === "qolo" && record.status !== "SPLITTED"
      )
      .reduce((sum, record) => sum + record.amount, 0);

  const formattedAmountQolo =
    totalQolo &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalQolo);

  const totalHighnote =
    dataPaymentsDetail &&
    dataPaymentsDetail?.data
      .filter(
        (record) =>
          record.processorProvider === "highnote" &&
          record.status !== "SPLITTED"
      )
      .reduce((sum, record) => sum + record.amount, 0);

  const formattedAmountHighnote =
    totalHighnote &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalHighnote);

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      disableEscapeKeyDown={true}
      sx={{
        "& .MuiPaper-root": {
          maxWidth: window.innerWidth - 50,
        },
      }}
    >
      <Box component="form" noValidate autoComplete="off">
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ArticleIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {paymentInfo && paymentInfo.description}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginLeft: 2,
                color: "white",
              }}
            >
              <BusinessIcon sx={{ fontSize: "1.2rem" }} />
              <Typography sx={{ fontSize: "0.9rem" }}>
                {Labels.getLabel("payments.company", "Company")}:{" "}
                {paymentInfo.employerName} [{paymentInfo.idEmployer}]
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "white" }}
            disabled={isFetchingPaymentsDetail}
          >
            {isFetchingPaymentsDetail ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <CloseIcon />
            )}
          </IconButton>
        </Box>

        <DialogContent
          sx={{ width: window.innerWidth - 150, flexShrink: 0, paddingTop: 3 }}
        >
          {isFetchingPaymentsDetail ? (
            <CircularProgress color="inherit" />
          ) : !isErrorPaymentsDetail ? (
            <Fragment>
              <Fragment>
                <Grid
                  style={{
                    marginLeft: "20px",
                    marginRight: "10px",
                    marginTop: 0,
                  }}
                  container
                  spacing={1.5}
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={1}>
                      <Typography>
                        {Labels.getLabel(
                          "payments.totalAmount",
                          "Total Amount:"
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <PaidIcon style={{ paddingRight: "5px" }} />
                          {paymentInfo && paymentInfo.totalAmount}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography>
                        {Labels.getLabel(
                          "payments.totalRecords",
                          "Total Records:"
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <NumbersIcon style={{ paddingRight: "5px" }} />
                          {paymentInfo && paymentInfo.numberOfRecords}
                        </Box>
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      spacing={1.5}
                      style={{
                        //marginLeft: "10px",
                        marginRight: "10px",
                        marginTop: "10px",
                        width: "50%",
                        marginLeft: "20px",
                      }}
                    >
                      <Grid container spacing={1.5}>
                        <Grid item xs={2} />
                        <Grid item xs={1}>
                          <Typography fontSize="1rem">
                            {Labels.getLabel("payments.success", "Success:")}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography fontSize="1rem">
                            {formattedAmountSuccess && formattedAmountSuccess}
                          </Typography>
                        </Grid>
                        <Grid item xs={0.5}>
                          <Typography fontSize="1rem">
                            {Labels.getLabel("payments.count", "#")}:
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography fontSize="1rem">
                            {totalCountSuccess}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container spacing={1.5}>
                        <Grid item xs={2} />
                        <Grid item xs={1}>
                          <Typography fontSize="1rem">
                            {Labels.getLabel("payments.pending", "Pending:")}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography fontSize="1rem">
                            {formattedAmountPending && formattedAmountPending}
                          </Typography>
                        </Grid>
                        <Grid item xs={0.5}>
                          <Typography fontSize="1rem">
                            {Labels.getLabel("payments.count", "#")}:
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography fontSize="1rem">
                            {totalCountPending}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <RenderOnRole roles={["pc-program-super-admin", "bl-crud"]}>
                      <Grid
                        container
                        spacing={1.5}
                        style={{
                          //marginLeft: "10px",
                          marginRight: "10px",
                          marginTop: "10px",
                          width: "50%",
                          marginLeft: "20px",
                        }}
                      >
                        <Typography fontSize="1rem" fontWeight="bold">
                          {Labels.getLabel(
                            "payments.totalAmountByProcessor",
                            "Total Amount By Processor:"
                          )}
                        </Typography>
                        {totalVisaDps > 0 && (
                          <Grid container spacing={3}>
                            <Grid item xs={1.5}>
                              <Typography fontSize="1rem">
                                {Labels.getLabel(
                                  "payments.visaDps",
                                  "Visa DPS:"
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography fontSize="1rem">
                                {formattedAmountVisaDps}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                        {totalQolo > 0 && (
                          <Grid container spacing={3}>
                            <Grid item xs={1.5}>
                              <Typography fontSize="1rem">
                                {Labels.getLabel("payments.qolo", "Qolo:")}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography fontSize="1rem">
                                {formattedAmountQolo}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                        {totalHighnote > 0 && (
                          <Grid container spacing={3}>
                            <Grid item xs={1.5}>
                              <Typography fontSize="1rem">
                                {Labels.getLabel(
                                  "payments.highnote",
                                  "Highnote:"
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography fontSize="1rem">
                                {formattedAmountHighnote}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    </RenderOnRole>
                  </Grid>

                  <Divider style={{ margin: "15px" }} />
                </Grid>

                <div style={{ height: 550, width: window.innerWidth - 300 }}>
                  <DataGrid
                    columnVisibilityModel={{ id: false }}
                    disableSelectionOnClick
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    rows={dataPaymentsDetail?.data}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10]}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                      },
                    }}
                    getRowHeight={() => 35}
                    onCellEditStop={(params, event) =>
                      handleEditCellChange(
                        params.row,
                        params.id,
                        params.field,
                        params.value,
                        event?.target?.value
                      )
                    }
                  />
                </div>

                <Snackbar
                  open={feedback.open}
                  autoHideDuration={6000}
                  onClose={
                    feedback.onClose ? feedback.onClose : handleFeedbackClose
                  }
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <Alert
                    onClose={
                      feedback.onClose ? feedback.onClose : handleFeedbackClose
                    }
                    severity={feedback.type}
                    sx={{ width: "100%" }}
                  >
                    {feedback.message}
                  </Alert>
                </Snackbar>
              </Fragment>
            </Fragment>
          ) : (
            <h4>
              {Labels.getLabel(
                "payments.error",
                "An error ocurred, please try again later..."
              )}
            </h4>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
}
